import { useGetLocale, useTranslate } from "@refinedev/core";

import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";
import { datumMetJaar, leeftijd } from "./hulp";
import type { PensionDier } from "../pensionKlantTypes";

/**
 * De vaste gegevens van het dier als onderdeel van een samengestelde tegel: soort, ras, geslacht,
 * leeftijd, kenmerken en bijzonderheden.
 *
 * <p>Zonder knop. Deze velden wijzig je op de basisgegevens van het dier, en daar hoort ook het
 * dier-schrijfrecht bij; een potlood per veld op de tegel zou een tweede invoerscherm worden voor
 * dezelfde gegevens. Lezen is hier het doel: wie de tegel bekijkt wil zien wie het dier is.
 *
 * <p>Alles komt uit het dier dat het scherm al heeft; er gaat geen eigen bevraging uit.
 */

export function DiersoortOnderdeel({ dier }: { dier: PensionDier }) {
  return <DierOnderdeel def={COMPONENT_PER_SLEUTEL.diersoort} waarde={dier.diersoort?.trim() || null} />;
}

export function RasOnderdeel({ dier }: { dier: PensionDier }) {
  return <DierOnderdeel def={COMPONENT_PER_SLEUTEL.ras} waarde={dier.ras?.trim() || null} />;
}

/** Geslacht, met eronder of het dier gecastreerd of gesteriliseerd is. */
export function GeslachtOnderdeel({ dier }: { dier: PensionDier }) {
  const t = useTranslate();
  const geslacht = dier.geslacht?.trim() || null;
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.geslacht}
      waarde={geslacht}
      herkomst={geslacht
        ? t(dier.gecastreerd ? "pensionKlant.dier.gecastreerdJa" : "pensionKlant.dier.gecastreerdNee")
        : undefined}
    />
  );
}

/** De leeftijd groot, de geboortedatum eronder: "6 jaar" zegt meer dan een datum. */
export function LeeftijdOnderdeel({ dier, vandaag }: { dier: PensionDier; vandaag: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const jaren = leeftijd(dier.geboortedatum, { t, locale, vandaag });
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.leeftijd}
      waarde={jaren}
      herkomst={jaren && dier.geboortedatum
        ? t("dierTegel.leeftijd.geboren", { args: [datumMetJaar(dier.geboortedatum, locale)] })
        : undefined}
    />
  );
}

export function KenmerkenOnderdeel({ dier }: { dier: PensionDier }) {
  const kenmerken = dier.kenmerken.filter((k) => k.trim() !== "");
  return (
    <DierOnderdeel
      def={COMPONENT_PER_SLEUTEL.kenmerken}
      waarde={kenmerken.length > 0 ? kenmerken.join(", ") : null}
    />
  );
}

export function BijzonderhedenOnderdeel({ dier }: { dier: PensionDier }) {
  return <DierOnderdeel def={COMPONENT_PER_SLEUTEL.bijzonderheden} waarde={dier.bijzonderheden?.trim() || null} />;
}
